import type { CropRect, ToolId } from '../types';

export type CropHandle = 'move' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

interface CropAdjustmentOptions {
  aspect?: number;
  minWidth?: number;
  minHeight?: number;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export function adjustCropRect(
  start: CropRect,
  handle: CropHandle,
  deltaX: number,
  deltaY: number,
  { aspect = 0, minWidth = .02, minHeight = .02 }: CropAdjustmentOptions = {}
): CropRect {
  if (handle === 'move') {
    return {
      ...start,
      x: clamp(start.x + deltaX, 0, 1 - start.width),
      y: clamp(start.y + deltaY, 0, 1 - start.height)
    };
  }

  const west = handle.includes('w');
  const east = handle.includes('e');
  const north = handle.includes('n');
  const south = handle.includes('s');
  const startRight = start.x + start.width;
  const startBottom = start.y + start.height;

  if (!aspect) {
    const left = west ? clamp(start.x + deltaX, 0, startRight - minWidth) : start.x;
    const right = east ? clamp(startRight + deltaX, start.x + minWidth, 1) : startRight;
    const top = north ? clamp(start.y + deltaY, 0, startBottom - minHeight) : start.y;
    const bottom = south ? clamp(startBottom + deltaY, start.y + minHeight, 1) : startBottom;
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  const safeAspect = Math.max(.01, aspect);
  const minimumWidth = Math.max(minWidth, minHeight * safeAspect);
  const minimumHeight = minimumWidth / safeAspect;

  if ((west || east) && (north || south)) {
    const anchorX = west ? startRight : start.x;
    const anchorY = north ? startBottom : start.y;
    const movingX = (west ? start.x : startRight) + deltaX;
    const movingY = (north ? start.y : startBottom) + deltaY;
    let width = Math.abs(movingX - anchorX);
    let height = Math.abs(movingY - anchorY);
    if (width / Math.max(height, .0001) > safeAspect) height = width / safeAspect;
    else width = height * safeAspect;
    const maxWidth = west ? anchorX : 1 - anchorX;
    const maxHeight = north ? anchorY : 1 - anchorY;
    const fit = Math.min(1, maxWidth / Math.max(width, .0001), maxHeight / Math.max(height, .0001));
    width = clamp(width * fit, Math.min(minimumWidth, maxWidth), maxWidth);
    height = width / safeAspect;
    if (height > maxHeight) { height = maxHeight; width = height * safeAspect; }
    return {
      x: west ? anchorX - width : anchorX,
      y: north ? anchorY - height : anchorY,
      width,
      height
    };
  }

  if (west || east) {
    const anchorX = west ? startRight : start.x;
    const centerY = start.y + start.height / 2;
    const movingX = (west ? start.x : startRight) + deltaX;
    const maxWidth = Math.min(west ? anchorX : 1 - anchorX, 2 * Math.min(centerY, 1 - centerY) * safeAspect);
    const width = clamp(Math.abs(movingX - anchorX), Math.min(minimumWidth, maxWidth), maxWidth);
    const height = width / safeAspect;
    return { x: west ? anchorX - width : anchorX, y: centerY - height / 2, width, height };
  }

  const anchorY = north ? startBottom : start.y;
  const centerX = start.x + start.width / 2;
  const movingY = (north ? start.y : startBottom) + deltaY;
  const maxHeight = Math.min(north ? anchorY : 1 - anchorY, 2 * Math.min(centerX, 1 - centerX) / safeAspect);
  const height = clamp(Math.abs(movingY - anchorY), Math.min(minimumHeight, maxHeight), maxHeight);
  const width = height * safeAspect;
  return { x: centerX - width / 2, y: north ? anchorY - height : anchorY, width, height };
}

export function toolSupportsCanvasInteraction(tool: ToolId) {
  return ['move', 'crop', 'brush', 'heal', 'liquify'].includes(tool);
}
